import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, 
  Receipt, 
  CheckCircle, 
  XCircle, 
  Clock,
  Search,
  Eye,
  Filter
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminBoletas() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [boletas, setBoletas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEstado, setFilterEstado] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTiendaId, setFilterTiendaId] = useState('all');
  const [filterBanco, setFilterBanco] = useState('all');
  const [stores, setStores] = useState([]);
  const [bancos, setBancos] = useState([]);
  // Por defecto mostrar solo boletas de hoy
  const today = new Date().toISOString().split('T')[0];
  const [filterFechaDesde, setFilterFechaDesde] = useState(today);
  const [filterFechaHasta, setFilterFechaHasta] = useState(today);
  const [filterMode, setFilterMode] = useState('hoy'); // 'hoy', 'semana', 'mes', 'personalizado'
  const [selectedBoleta, setSelectedBoleta] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState('');
  const [verifyAction, setVerifyAction] = useState('');

  useEffect(() => {
    fetchBoletas();
  }, [filterEstado, filterFechaDesde, filterFechaHasta, filterTiendaId, filterBanco]);

  useEffect(() => {
    // Cargar catálogos para los dropdowns
    axios.get(`${API}/stores`).then(r => setStores(r.data || [])).catch(() => {});
    axios.get(`${API}/bancos`).then(r => setBancos(r.data || [])).catch(() => {});
  }, []);

  const handleFilterModeChange = (mode) => {
    setFilterMode(mode);
    const now = new Date();
    
    switch(mode) {
      case 'hoy':
        const todayStr = now.toISOString().split('T')[0];
        setFilterFechaDesde(todayStr);
        setFilterFechaHasta(todayStr);
        break;
      case 'ayer':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];
        setFilterFechaDesde(yesterdayStr);
        setFilterFechaHasta(yesterdayStr);
        break;
      case 'semana':
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        setFilterFechaDesde(weekAgo.toISOString().split('T')[0]);
        setFilterFechaHasta(now.toISOString().split('T')[0]);
        break;
      case 'mes':
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        setFilterFechaDesde(monthAgo.toISOString().split('T')[0]);
        setFilterFechaHasta(now.toISOString().split('T')[0]);
        break;
      case 'todo':
        setFilterFechaDesde('');
        setFilterFechaHasta('');
        break;
      case 'personalizado':
        // Keep current values, user will set manually
        break;
      default:
        break;
    }
  };

  const fetchBoletas = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEstado !== 'all') {
        params.append('estado', filterEstado);
      }
      if (filterFechaDesde) {
        params.append('fecha_desde', filterFechaDesde);
      }
      if (filterFechaHasta) {
        params.append('fecha_hasta', filterFechaHasta);
      }
      if (filterTiendaId && filterTiendaId !== 'all') {
        params.append('tienda_id', filterTiendaId);
      }
      if (filterBanco && filterBanco !== 'all') {
        params.append('banco', filterBanco);
      }
      
      const response = await axios.get(`${API}/boletas?${params.toString()}`);
      setBoletas(response.data);
    } catch (error) {
      console.error('Error al cargar boletas:', error);
      toast.error('Error al cargar boletas');
    } finally {
      setLoading(false);
    }
  };

  const handleViewBoleta = async (boletaId) => {
    try {
      const response = await axios.get(`${API}/boletas/${boletaId}`);
      setSelectedBoleta(response.data);
      setShowModal(true);
      setVerifyNotes('');
      setVerifyAction('');
    } catch (error) {
      console.error('Error al cargar boleta:', error);
      toast.error('Error al cargar detalles de la boleta');
    }
  };

  const handleVerifyBoleta = async (action) => {
    if (!selectedBoleta) return;
    
    try {
      await axios.post(
        `${API}/boletas/${selectedBoleta.id}/verify`,
        {
          estado: action,
          notas: verifyNotes
        }
      );
      
      toast.success(
        action === 'verificada' 
          ? '✅ Boleta verificada exitosamente' 
          : '❌ Boleta rechazada'
      );
      
      setShowModal(false);
      setSelectedBoleta(null);
      fetchBoletas();
    } catch (error) {
      console.error('Error al verificar boleta:', error);
      toast.error('Error al verificar la boleta');
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'verificada':
        return <Badge className="bg-green-100 text-green-800 border-green-300">Verificada</Badge>;
      case 'rechazada':
        return <Badge className="bg-red-100 text-red-800 border-red-300">Rechazada</Badge>;
      case 'pendiente':
      default:
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">Pendiente</Badge>;
    }
  };

  const filteredBoletas = boletas.filter(boleta => {
    const matchesSearch = 
      boleta.numero_boleta.toLowerCase().includes(searchTerm.toLowerCase()) ||
      boleta.banco.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (boleta.tienda_nombre && boleta.tienda_nombre.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Filter by fecha desde
    if (filterFechaDesde && boleta.fecha < filterFechaDesde) {
      return false;
    }
    
    // Filter by fecha hasta
    if (filterFechaHasta && boleta.fecha > filterFechaHasta) {
      return false;
    }
    
    return matchesSearch;
  });

  const canVerify = user?.role === 'admin' || user?.role === 'gerencia';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver al Dashboard
          </Button>
          <div className="flex items-center space-x-3 mb-2">
            <div className="bg-blue-100 rounded-full p-3">
              <Receipt className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Gestión de Boletas</h1>
              <p className="text-slate-600">Administra y verifica las boletas de depósito</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-6 mb-6">
          {/* Quick date filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-sm font-medium text-slate-600 self-center mr-2">Período:</span>
            {[
              { key: 'hoy', label: 'Hoy' },
              { key: 'ayer', label: 'Ayer' },
              { key: 'semana', label: 'Última Semana' },
              { key: 'mes', label: 'Último Mes' },
              { key: 'todo', label: 'Todo' },
              { key: 'personalizado', label: 'Personalizado' },
            ].map(({ key, label }) => (
              <Button
                key={key}
                variant={filterMode === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFilterModeChange(key)}
                className={filterMode === key ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                {label}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="search"
                  placeholder="Número, banco, tienda..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="estado">Estado</Label>
              <Select value={filterEstado} onValueChange={setFilterEstado}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="verificada">Verificada</SelectItem>
                  <SelectItem value="rechazada">Rechazada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tienda">Tienda</Label>
              <Select value={filterTiendaId} onValueChange={setFilterTiendaId}>
                <SelectTrigger data-testid="filter-tienda-boletas">
                  <SelectValue placeholder="Todas las tiendas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las tiendas</SelectItem>
                  {stores.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="banco">Banco</Label>
              <Select value={filterBanco} onValueChange={setFilterBanco}>
                <SelectTrigger data-testid="filter-banco-boletas">
                  <SelectValue placeholder="Todos los bancos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los bancos</SelectItem>
                  {bancos.map(b => (
                    <SelectItem key={b.id || b.nombre} value={b.nombre}>{b.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fecha-desde">Fecha Desde</Label>
              <Input
                id="fecha-desde"
                type="date"
                value={filterFechaDesde}
                onChange={(e) => {
                  setFilterFechaDesde(e.target.value);
                  setFilterMode('personalizado');
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fecha-hasta">Fecha Hasta</Label>
              <Input
                id="fecha-hasta"
                type="date"
                value={filterFechaHasta}
                onChange={(e) => {
                  setFilterFechaHasta(e.target.value);
                  setFilterMode('personalizado');
                }}
              />
            </div>

            <div className="lg:col-span-2 flex items-end justify-between gap-2 flex-wrap">
              <Badge variant="outline" className="text-sm py-2 px-3">
                {filteredBoletas.length} boleta{filteredBoletas.length !== 1 ? 's' : ''}
              </Badge>
              {(filterTiendaId !== 'all' || filterBanco !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setFilterTiendaId('all'); setFilterBanco('all'); }}
                  className="text-xs text-slate-500"
                  data-testid="clear-tienda-banco"
                >
                  Limpiar tienda/banco ✕
                </Button>
              )}
            </div>
          </div>
          
          {(filterFechaDesde || filterFechaHasta) && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm text-slate-600">Filtros activos:</span>
              {filterFechaDesde && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilterFechaDesde('')}
                  className="h-7 text-xs"
                >
                  Desde: {filterFechaDesde} ✕
                </Button>
              )}
              {filterFechaHasta && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilterFechaHasta('')}
                  className="h-7 text-xs"
                >
                  Hasta: {filterFechaHasta} ✕
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total</p>
                <p className="text-2xl font-bold text-slate-900">{boletas.length}</p>
              </div>
              <Receipt className="h-8 w-8 text-blue-600" />
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Pendientes</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {boletas.filter(b => b.estado === 'pendiente').length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Verificadas</p>
                <p className="text-2xl font-bold text-green-600">
                  {boletas.filter(b => b.estado === 'verificada').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Rechazadas</p>
                <p className="text-2xl font-bold text-red-600">
                  {boletas.filter(b => b.estado === 'rechazada').length}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </Card>
        </div>

        {/* Analytics Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Por Banco */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Depósitos por Banco</h3>
            <div className="space-y-3">
              {Object.entries(
                boletas.reduce((acc, b) => {
                  acc[b.banco] = (acc[b.banco] || 0) + b.monto;
                  return acc;
                }, {})
              )
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([banco, total]) => (
                  <div key={banco} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                    <span className="font-medium text-slate-700">{banco}</span>
                    <span className="text-lg font-bold text-green-600">Q{total.toFixed(2)}</span>
                  </div>
                ))}
              {Object.keys(boletas.reduce((acc, b) => {
                acc[b.banco] = true;
                return acc;
              }, {})).length === 0 && (
                <p className="text-slate-500 text-center py-4">No hay datos disponibles</p>
              )}
            </div>
          </Card>

          {/* Por Tienda */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Depósitos por Tienda</h3>
            <div className="space-y-3">
              {Object.entries(
                boletas
                  .filter(b => b.tienda_nombre)
                  .reduce((acc, b) => {
                    acc[b.tienda_nombre] = (acc[b.tienda_nombre] || 0) + b.monto;
                    return acc;
                  }, {})
              )
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([tienda, total]) => (
                  <div key={tienda} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                    <span className="font-medium text-slate-700">{tienda}</span>
                    <span className="text-lg font-bold text-blue-600">Q{total.toFixed(2)}</span>
                  </div>
                ))}
              {boletas.filter(b => b.tienda_nombre).length === 0 && (
                <p className="text-slate-500 text-center py-4">No hay datos disponibles</p>
              )}
            </div>
          </Card>

          {/* Por Usuario */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Top Usuarios</h3>
            <div className="space-y-3">
              {Object.entries(
                boletas.reduce((acc, b) => {
                  const name = b.created_by_name || 'Sin nombre';
                  acc[name] = (acc[name] || 0) + b.monto;
                  return acc;
                }, {})
              )
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([usuario, total]) => (
                  <div key={usuario} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                    <span className="font-medium text-slate-700">{usuario}</span>
                    <span className="text-lg font-bold text-purple-600">Q{total.toFixed(2)}</span>
                  </div>
                ))}
              {boletas.length === 0 && (
                <p className="text-slate-500 text-center py-4">No hay datos disponibles</p>
              )}
            </div>
          </Card>

          {/* Resumen General */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Resumen de Depósitos</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <p className="text-sm text-green-700 font-medium">Total Depositado</p>
                  <p className="text-3xl font-bold text-green-900">
                    Q{boletas.reduce((sum, b) => sum + b.monto, 0).toFixed(2)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <p className="text-sm text-blue-700 font-medium">Verificado</p>
                  <p className="text-2xl font-bold text-blue-900">
                    Q{boletas.filter(b => b.estado === 'verificada').reduce((sum, b) => sum + b.monto, 0).toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div>
                  <p className="text-sm text-yellow-700 font-medium">Pendiente</p>
                  <p className="text-2xl font-bold text-yellow-900">
                    Q{boletas.filter(b => b.estado === 'pendiente').reduce((sum, b) => sum + b.monto, 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Boletas List */}
        <div className="space-y-4">
          {loading ? (
            <Card className="p-8 text-center">
              <p className="text-slate-600">Cargando boletas...</p>
            </Card>
          ) : filteredBoletas.length === 0 ? (
            <Card className="p-8 text-center">
              <Receipt className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">No hay boletas para mostrar</p>
            </Card>
          ) : (
            filteredBoletas.map((boleta) => (
              <Card key={boleta.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-lg font-semibold text-slate-900">
                        Boleta #{boleta.numero_boleta}
                      </h3>
                      {getEstadoBadge(boleta.estado)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="font-medium text-slate-600">Banco:</span>
                        <p className="text-slate-900">{boleta.banco}</p>
                      </div>
                      <div>
                        <span className="font-medium text-slate-600">Fecha:</span>
                        <p className="text-slate-900">{boleta.fecha}</p>
                      </div>
                      <div>
                        <span className="font-medium text-slate-600">Monto:</span>
                        <p className="text-slate-900 font-semibold">Q{boleta.monto.toFixed(2)}</p>
                      </div>
                      {boleta.tienda_nombre && (
                        <div>
                          <span className="font-medium text-slate-600">Tienda:</span>
                          <p className="text-slate-900">{boleta.tienda_nombre}</p>
                        </div>
                      )}
                      {boleta.sucursal && (
                        <div>
                          <span className="font-medium text-slate-600">Quien lo envía:</span>
                          <p className="text-slate-900">{boleta.sucursal}</p>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-slate-600">Creado por:</span>
                        <p className="text-slate-900">{boleta.created_by_name}</p>
                      </div>
                      <div>
                        <span className="font-medium text-slate-600">Registrado:</span>
                        <p className="text-slate-900">
                          {format(new Date(boleta.created_at), 'dd MMM yyyy HH:mm', { locale: es })}
                        </p>
                      </div>
                      {boleta.verified_by_name && (
                        <div>
                          <span className="font-medium text-slate-600">Verificado por:</span>
                          <p className="text-slate-900">{boleta.verified_by_name}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={() => handleViewBoleta(boleta.id)}
                    variant="outline"
                    size="sm"
                    className="ml-4"
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Ver Detalle
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Modal for Boleta Detail */}
      {showModal && selectedBoleta && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Receipt className="h-6 w-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-slate-900">
                    Boleta #{selectedBoleta.numero_boleta}
                  </h2>
                  {getEstadoBadge(selectedBoleta.estado)}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowModal(false)}
                >
                  ✕
                </Button>
              </div>

              {/* Boleta Details */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="text-slate-600">Banco</Label>
                  <p className="text-lg font-medium">{selectedBoleta.banco}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Fecha de Depósito</Label>
                  <p className="text-lg font-medium">{selectedBoleta.fecha}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Monto</Label>
                  <p className="text-lg font-medium text-green-600">Q{selectedBoleta.monto.toFixed(2)}</p>
                </div>
                {selectedBoleta.sucursal && (
                  <div>
                    <Label className="text-slate-600">Quien lo envía</Label>
                    <p className="text-lg font-medium">{selectedBoleta.sucursal}</p>
                  </div>
                )}
                {selectedBoleta.tienda_nombre && (
                  <div>
                    <Label className="text-slate-600">Tienda</Label>
                    <p className="text-lg font-medium">{selectedBoleta.tienda_nombre}</p>
                  </div>
                )}
                <div>
                  <Label className="text-slate-600">Creado por</Label>
                  <p className="text-lg font-medium">{selectedBoleta.created_by_name}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Fecha de Registro</Label>
                  <p className="text-lg font-medium">
                    {format(new Date(selectedBoleta.created_at), 'dd MMM yyyy HH:mm', { locale: es })}
                  </p>
                </div>
              </div>

              {/* Image */}
              {selectedBoleta.imagen_url && (
                <div className="mb-6">
                  <Label className="text-slate-600 mb-2 block">Imagen de la Boleta</Label>
                  <img
                    src={selectedBoleta.imagen_url}
                    alt="Boleta"
                    className="w-full rounded-lg border border-slate-200"
                  />
                </div>
              )}

              {/* Verification Notes */}
              {selectedBoleta.notas_verificacion && (
                <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                  <Label className="text-slate-600 mb-2 block">Notas de Verificación</Label>
                  <p className="text-slate-900">{selectedBoleta.notas_verificacion}</p>
                  {selectedBoleta.verified_by_name && (
                    <p className="text-sm text-slate-600 mt-2">
                      Por {selectedBoleta.verified_by_name} el{' '}
                      {format(new Date(selectedBoleta.verified_at), 'dd MMM yyyy HH:mm', { locale: es })}
                    </p>
                  )}
                </div>
              )}

              {/* Verification Actions (only for pending boletas and authorized users) */}
              {selectedBoleta.estado === 'pendiente' && canVerify && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="verify-notes">Notas (opcional)</Label>
                    <Input
                      id="verify-notes"
                      placeholder="Agrega notas sobre la verificación..."
                      value={verifyNotes}
                      onChange={(e) => setVerifyNotes(e.target.value)}
                      className="mt-2"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      onClick={() => handleVerifyBoleta('verificada')}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Verificar y Aprobar
                    </Button>
                    <Button
                      onClick={() => handleVerifyBoleta('rechazada')}
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Rechazar
                    </Button>
                  </div>
                </div>
              )}

              {/* Close Button */}
              {selectedBoleta.estado !== 'pendiente' && (
                <div className="flex justify-end">
                  <Button
                    onClick={() => setShowModal(false)}
                    variant="outline"
                  >
                    Cerrar
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
